// The one way a staff sign-in page starts social sign-in (BL-2090, ADR 064 —
// Native Shell § 4): Better Auth's redirect in a browser, the system-browser
// handoff plus a one-time-token exchange inside the native app.
import { api } from '@auctogon/admin-frontend/api-client/typed-client';
import {
  getNativeDeviceGateway,
  NativeCapabilityUnavailableError,
  type NativeDeviceGateway,
} from '@auctogon/admin-frontend/gateways/native-device';
import { safeReturnUrl } from '@auctogon/core/validation/safe-return-url';
import type { SocialProviderId } from '@auctogon/core-admin/auth/social-providers';
import { EMAIL_NOT_VERIFIED_MESSAGE } from '@auctogon/core-admin/auth/staff-sign-in-errors';
import {
  NATIVE_EMAIL_NOT_VERIFIED_ERROR,
  NATIVE_NO_STAFF_ACCESS_ERROR,
  NATIVE_SIGN_IN_FAILED_MESSAGE,
} from '@auctogon/core-admin/schemas/native-auth';

import { authClient } from '@/lib/auth/client';

/** The social providers a staff member can sign in with (BL-2776: one list, in core-admin). */
export type SocialProvider = SocialProviderId;

/** Where the native app's browser sheet starts a provider (BL-2065, BL-2776). */
function nativeStartPath(provider: SocialProvider): string {
  return `/auth/native/${provider}/start`;
}

/**
 * How starting social sign-in ended, from the caller's point of view.
 * - `redirecting`: the page is navigating away; keep the spinner.
 * - `cancelled`: the user closed the app's browser sheet; show nothing.
 * - `no_access`: the invite gate refused to create a staff user for the social
 *   account (native app only; a browser gets this as `?error=` on `errorCallbackURL`).
 * - `error`: show `message`, or the caller's own copy when it is undefined.
 */
export type StartSocialSignInResult =
  | { status: 'redirecting' }
  | { status: 'cancelled' }
  | { status: 'no_access' }
  | { status: 'error'; message: string | undefined };

/** Input to {@link startSocialSignIn}. */
export interface StartSocialSignInInput {
  provider: SocialProvider;
  /** Same-origin path to land on once signed in. */
  callbackURL: string;
  /**
   * Same-origin path Better Auth returns to, with `error=<code>` appended, when a
   * browser sign-in is refused (BUG-2162). Without it a refusal lands on Better
   * Auth's bare error page. The native app reports a refusal through its own
   * return path instead.
   */
  errorCallbackURL: string;
  /**
   * The invite's single-use token (accept-invite only). Sent to Better Auth as
   * `additionalData` so the server gate can match an Apple private-relay email
   * to the invite (BL-2776). Browser path only: the native start URL carries no
   * token today, so a relay-email invitee cannot use Apple inside the app.
   */
  inviteToken?: string;
}

/**
 * Start social sign-in the way this shell supports it. In a browser this is
 * exactly Better Auth's `signIn.social` redirect. Inside the native app it opens
 * the provider in the phone's system browser sheet (Google refuses embedded web
 * views), exchanges the one-time token the sheet returns for a session cookie,
 * and hard-navigates to `callbackURL`. An app build without the auth-session
 * plugin falls back to the browser path.
 *
 * @param input - the provider and the same-origin return and error paths.
 * @returns what the caller should show next.
 * @throws when the native sheet itself fails, or the exchange request cannot be sent.
 * @sideEffect Navigates the page (Better Auth redirect or `location.assign`); in the
 *   app, opens a browser sheet and sets the staff session cookie.
 */
export async function startSocialSignIn({
  provider,
  callbackURL,
  errorCallbackURL,
  inviteToken,
}: StartSocialSignInInput): Promise<StartSocialSignInResult> {
  const gateway = await getNativeDeviceGateway();
  if (gateway.isNativeApp()) {
    try {
      return await nativeSignIn({ gateway, provider, callbackURL });
    } catch (err) {
      // Only a missing plugin falls back; any other failure is real (rule #6).
      if (!(err instanceof NativeCapabilityUnavailableError)) throw err;
    }
  }
  return webSignIn({ provider, callbackURL, errorCallbackURL, inviteToken });
}

async function webSignIn({
  provider,
  callbackURL,
  errorCallbackURL,
  inviteToken,
}: StartSocialSignInInput): Promise<StartSocialSignInResult> {
  const result = await authClient.signIn.social({
    provider,
    callbackURL,
    errorCallbackURL,
    ...(inviteToken ? { additionalData: { inviteToken } } : {}),
  });
  // signIn.social redirects on success; a returned error means it never did.
  if (result?.error) return { status: 'error', message: result.error.message };
  return { status: 'redirecting' };
}

async function nativeSignIn({
  gateway,
  provider,
  callbackURL,
}: Omit<StartSocialSignInInput, 'errorCallbackURL' | 'inviteToken'> & { gateway: NativeDeviceGateway }): Promise<StartSocialSignInResult> {
  const outcome = await gateway.openAuthSession({
    url: `${window.location.origin}${nativeStartPath(provider)}`,
  });
  if (outcome.kind === 'cancelled') return { status: 'cancelled' };

  const callback = readCallback(outcome.callbackUrl);
  if (callback.error === NATIVE_NO_STAFF_ACCESS_ERROR) return { status: 'no_access' };
  if (callback.error === NATIVE_EMAIL_NOT_VERIFIED_ERROR) {
    return { status: 'error', message: EMAIL_NOT_VERIFIED_MESSAGE };
  }
  const token = callback.token;
  if (token === null) return { status: 'error', message: NATIVE_SIGN_IN_FAILED_MESSAGE };

  const exchanged = await api.auth.native.exchange.post({ body: { token } });
  // The server's own copy: the generic rejection, or the rate-limit message.
  if (!exchanged.success) return { status: 'error', message: exchanged.error };

  window.location.assign(safeReturnUrl(callbackURL));
  return { status: 'redirecting' };
}

/**
 * What the sheet's `<scheme>://auth?…` callback carries: the one-time `token`,
 * or an `error` code. The `done` route sends `?error=1` and `start` sends
 * `?error=no_access` instead of a token, so a failure has no token. An
 * unparseable callback is one more failed sign-in: no token, no error.
 */
function readCallback(callbackUrl: string): { token: string | null; error: string | null } {
  let params: URLSearchParams;
  try {
    params = new URL(callbackUrl).searchParams;
  } catch {
    return { token: null, error: null };
  }
  const token = params.get('token');
  return { token: token ? token : null, error: params.get('error') };
}
